// Basketball — Dashboard (Overview). REBUILT around the live product (founder
// ruling 2026-05-29): the old port showed fabricated demo numbers (208/750
// closing lines, 2,841 shots tagged, a fake "live" closing-line ticker) that
// were never wired to data. This dashboard now reads ONLY real sources:
//   • BBALL SINGLES reads  (useBballSingles → get_bball_singles)
//   • the live slate       (useBasketballBoard → get_basketball_board)
//   • real coverage counts (useBballDashboardStats → get_basketball_dashboard_stats)
// Calibration status stays as an honest secondary strip. No fabricated numbers,
// no shimmer placeholders pretending to load — empty is shown as empty.

import { Link } from "react-router-dom";
import { BbHead, BIcon, fmtOdds, fmtTip } from "./_bbkit.tsx";
import { SkeletonText, SkeletonStat } from "../../components/skeletons/Skeleton.tsx";
import { useBasketballBoard } from "./useBasketballBoard.ts";
import { useBballSingles, bballPickTarget } from "./useBballSingles.ts";
import { useBballDashboardStats } from "./useBballDashboardStats.ts";

function StatTile({ v, label, hint }: { v: number | string; label: string; hint?: string }): JSX.Element {
  return (
    <div className="bb-score">
      <div className="top"><span className="pip on" /><span className="lab">{label}</span></div>
      <div className="big">{v}{hint ? <span className="unit">{hint}</span> : null}</div>
    </div>
  );
}

export function OverviewScreen(): JSX.Element {
  const { games, loading: slateLoading } = useBasketballBoard();
  const { picks, loading: readsLoading } = useBballSingles();
  const { stats } = useBballDashboardStats();

  const slateCount = games.length;
  const readCount = picks.length;
  const edgeCount = picks.filter((p) => p.isEdge).length;
  const topRead = picks[0];
  const nextGame = [...games].sort((a, b) => (a.tipoffAt ?? "").localeCompare(b.tipoffAt ?? ""))[0];

  return (
    <div className="bb" data-screen-label="overview">
      <BbHead kicker="DASHBOARD" title="Basketball" />

      <div className="bb-hero">
        <div>
          <div className="bb-hero-kicker">The hardwood edge</div>
          <h1>A sharp read on <em>every</em> game.</h1>
          <p className="lede">
            BBALL SINGLES prices each upcoming game off the sharp (Pinnacle) line, or the
            market consensus when the sharp isn't out yet, and surfaces one read near even
            odds. Genuine soft-beats-sharp outliers are flagged EDGE. Every pick is
            provisional — slip-live stays gated until realized CLV earns it.
          </p>
          <div className="bb-hero-chips">
            <span className="bb-chip"><span className="d" />Pinnacle-anchored</span>
            <span className="bb-chip"><span className="d" />Spread · Total · Moneyline</span>
            <span className="bb-chip"><span className="d" />CLV-clean</span>
          </div>
        </div>
        <div className="bb-hero-aside">
          <div className="bb-deflist">
            <div className="bb-defrow"><div className="k">State</div><div className="v"><span className="pip" />Calibrating</div></div>
            <div className="bb-defrow"><div className="k">Slip-live</div><div className="v dim">No — gated</div></div>
            <div className="bb-defrow"><div className="k">Edge source</div><div className="v">Soft vs sharp line</div></div>
            <div className="bb-defrow"><div className="k">Closing lines</div><div className="v">{stats ? stats.closingLines.toLocaleString() : "—"}</div></div>
          </div>
        </div>
      </div>

      {/* Live product centerpiece: two real cards */}
      <div className="bb-grid-2" style={{ marginBottom: 24 }}>
        <div className="bb-card" style={{ padding: 0 }}>
          <div className="bb-card-body">
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", color: "var(--bb-orange-text)", textTransform: "uppercase" }}>BBALL SINGLES</div>
              <Link to="/exchange/basketball/singles" style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--bb-orange-text)", textDecoration: "none" }}>Open →</Link>
            </div>
            {readsLoading ? (
              <div style={{ marginTop: 12 }}><SkeletonText lines={3} /></div>
            ) : readCount === 0 ? (
              <div style={{ fontSize: 13.5, color: "var(--text-secondary)", marginTop: 12, lineHeight: 1.5 }}>
                No priced games in range right now — no reads to show. Reads return when the next slate is priced.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 18, marginTop: 10 }}>
                  <div style={{ fontFamily: "var(--bb-display)", fontSize: 48, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{readCount}</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-tertiary)", letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8 }}>
                    sharp read{readCount !== 1 ? "s" : ""}{edgeCount > 0 ? ` · ${edgeCount} EDGE` : ""}
                  </div>
                </div>
                {topRead ? (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-faint)" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-tertiary)", marginBottom: 4 }}>Top read</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{bballPickTarget(topRead)} <span style={{ color: "var(--bb-orange-text)", fontFamily: "var(--font-mono)", fontSize: 13 }}>@ {fmtOdds(topRead.softOdds)}</span></div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{topRead.home} v {topRead.away}{topRead.kickoffAt ? ` · ${fmtTip(topRead.kickoffAt)}` : ""}</div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="bb-card" style={{ padding: 0 }}>
          <div className="bb-card-body">
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", color: "var(--bb-orange-text)", textTransform: "uppercase" }}>Today's Slate</div>
              <Link to="/exchange/basketball/slate" style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--bb-orange-text)", textDecoration: "none" }}>Open →</Link>
            </div>
            {slateLoading ? (
              <div style={{ marginTop: 12 }}><SkeletonText lines={3} /></div>
            ) : slateCount === 0 ? (
              <div style={{ fontSize: 13.5, color: "var(--text-secondary)", marginTop: 12, lineHeight: 1.5 }}>
                No games on the board right now. Live odds appear here as fixtures are ingested.
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 18, marginTop: 10 }}>
                  <div style={{ fontFamily: "var(--bb-display)", fontSize: 48, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{slateCount}</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-tertiary)", letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8 }}>
                    game{slateCount !== 1 ? "s" : ""} on the board
                  </div>
                </div>
                {nextGame ? (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-faint)" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-tertiary)", marginBottom: 4 }}>Next tip-off</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{nextGame.home} v {nextGame.away}</div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{nextGame.league ?? "NBA"}{nextGame.tipoffAt ? ` · ${fmtTip(nextGame.tipoffAt)}` : ""}</div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Real coverage — counts only, honest zeros */}
      <div className="bb-scoreboard">
        {stats === null ? (
          <>
            <div className="bb-score"><SkeletonStat /></div>
            <div className="bb-score"><SkeletonStat /></div>
            <div className="bb-score"><SkeletonStat /></div>
            <div className="bb-score"><SkeletonStat /></div>
          </>
        ) : (
          <>
            <StatTile v={stats.upcomingGames} label="Upcoming games" />
            <StatTile v={stats.marketsLive} label="Markets live" hint="ml · spr · tot" />
            <StatTile v={stats.booksLive} label="Books tracked" hint="live" />
            <StatTile v={stats.closingLines.toLocaleString()} label="Closing lines stored" />
          </>
        )}
      </div>

      {/* Honest calibration status — secondary */}
      <div className="bb-card" style={{ marginTop: 24 }}>
        <div className="bb-card-head">
          <div className="t">Path to slip-live</div>
          <span className="ct">Server-side gated</span>
        </div>
        <div className="bb-card-body">
          <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.6, margin: 0, maxWidth: "70ch" }}>
            The basketball layer is live on real odds and publishing sharp reads, but it stays
            in calibration: we log every closing line and will not promote a pick to slip-live
            until realized CLV proves the edge over a sample we'll stand behind. Reads are
            marked PROVISIONAL until then — there is no fabricated track record here.
          </p>
        </div>
      </div>
    </div>
  );
}
